import { useMemo, useRef, useState } from 'react';

import type { CatalogueDesign } from '@/api/masters';
import { DesignPhoto } from '@/components/DesignPhoto';
import {
  axesByUnit,
  axisChips,
  byReference,
  garmentState,
  searchByDigits,
  searchByReference,
  shortNameRows,
  typedLength,
} from '@core/bookings/garmentSearch';
import type { AvailableUnit } from '@core/bookings/rail';
import { railScan } from '@core/bookings/railScan';
import { normalizeReference, withReference } from '@core/units/referenceNumber';

/**
 * The garment search on a new booking (canvas "Booking flow", 2026-10-26), the phone's way on one
 * screen: the short-name button on the left of the box opens the designs under it — photo, short
 * name, what is free — and once one is chosen the box takes only the digits on its tag
 * (`BJB-` then `3601`), the design's garments narrowing below with their photos and what stands in
 * the way of each. Enter adds the highlighted one. **Any** turns the box back into one for a
 * design's name — which narrows the cards below — or a whole code, which a scanner types and Enter
 * adds straight away. **RN** takes the shop's own number off an old tag (`RN-` then `7`), listing
 * every garment whose number starts with it (owner, 2026-10-28).
 */
export function GarmentSearch({
  designs,
  units,
  claimed,
  ownerOf,
  forName,
  disabled,
  query,
  onQuery,
  onAdd,
  onDesignChange,
  onChaseHold,
}: {
  designs: CatalogueDesign[];
  /** The rail for the dates — what the digits list and a scan read. */
  units: AvailableUnit[];
  claimed: ReadonlySet<number>;
  /** Whose bag a garment in this booking is in, or null. */
  ownerOf: (unitId: number) => string | null;
  /** Who an add goes to; null while nobody is chosen. */
  forName: string | null;
  disabled: boolean;
  /** What Any has typed — the design cards below narrow to it. */
  query: string;
  onQuery: (query: string) => void;
  onAdd: (unit: AvailableUnit) => void;
  /** A short name chosen, or Any again — the cards below give way to the digits list. */
  onDesignChange?: ((code: string | null) => void) | undefined;
  /** Another customer holds this garment and the clerk wants to ring them — the parent's drawer. */
  onChaseHold?: ((unit: AvailableUnit) => void) | undefined;
}) {
  const [code, setCode] = useState<string | null>(null);
  const [digits, setDigits] = useState('');
  const [picking, setPicking] = useState(false);
  const [filter, setFilter] = useState('');
  const [said, setSaid] = useState<{ tone: 'good' | 'bad'; message: string } | null>(null);
  const box = useRef<HTMLInputElement>(null);

  const names = shortNameRows(designs, units, claimed, filter);
  const axesOf = useMemo(() => axesByUnit(designs), [designs]);
  const byNumber = code === byReference;
  const chosen =
    code === null || byNumber
      ? null
      : (shortNameRows(designs, units, claimed).find((row) => row.code === code) ?? null);
  const { matches, next } =
    code === null
      ? { matches: [], next: null }
      : byNumber
        ? searchByReference(units, digits, claimed)
        : searchByDigits(designs, units, code, digits, claimed);
  const label = byNumber ? 'Shop’s no.' : code;
  // Board A of "RN search results" (owner, 2026-10-28): Free only by default, but a number typed
  // whole always shows its garment, so a booked one says who has it rather than "none".
  const [freeOnly, setFreeOnly] = useState(true);
  const stateOf = (unit: AvailableUnit) =>
    garmentState(unit, claimed.has(unit.unitId) ? ownerOf(unit.unitId) : null);
  const exact = byNumber ? normalizeReference(digits) : null;
  const listed = matches.filter(
    (unit) => !byNumber || !freeOnly || stateOf(unit).free || unit.referenceNumber === exact,
  );
  const forWhom = forName ?? 'them';

  const choose = (picked: string | null) => {
    setCode(picked);
    onDesignChange?.(picked);
    setDigits('');
    setPicking(false);
    setFilter('');
    setSaid(null);
    onQuery('');
    window.setTimeout(() => box.current?.focus(), 0);
  };

  const add = (unit: AvailableUnit) => {
    if (forName === null) {
      setSaid({ tone: 'bad', message: 'Choose who it is for first.' });
      return;
    }

    onAdd(unit);
    setSaid({
      tone: 'good',
      message: `${withReference(unit.serialCode, unit.referenceNumber)} added for ${forName}.`,
    });
    setDigits('');
    box.current?.focus();
  };

  // Enter: the highlighted garment of the chosen design, or a whole code read as a scan.
  const submit = () => {
    if (code !== null) {
      if (next) {
        add(next);
      }

      return;
    }

    const result = railScan(query, units);

    if (result === null) {
      return;
    }

    if (result.tone === 'bad') {
      setSaid(result);
    } else if (claimed.has(result.unit.unitId)) {
      setSaid({ tone: 'bad', message: `${result.unit.serialCode} is already in this booking.` });
    } else {
      add(result.unit);
      onQuery('');
    }
  };

  return (
    <div>
      <p className="mb-1.5 text-[13px] font-semibold text-gray-900">Design, then size and number</p>
      <div className="flex gap-2">
        <form
          data-enter-key-owns
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
          className="relative min-w-0 flex-1"
        >
          {/*
            One box, one focus: the forms plugin rings every focused input in its own blue, which
            drew a second box inside this one (2026-10-28). The inputs drop it; the box shows focus.
          */}
          <div className="border-primary focus-within:ring-primary/15 flex h-[52px] items-stretch overflow-hidden rounded-xl border-2 bg-white transition-shadow focus-within:ring-4">
            <button
              type="button"
              disabled={disabled}
              aria-expanded={picking}
              aria-label={
                byNumber
                  ? 'Searching by the shop’s number — change'
                  : 'Choose the design’s short name'
              }
              onClick={() => {
                setPicking((was) => !was);
                setFilter('');
              }}
              className={`inline-flex shrink-0 cursor-pointer items-center gap-1.5 px-4 font-bold tracking-wide focus-visible:outline-none ${
                code
                  ? 'bg-primary hover:bg-primary-hover text-white'
                  : 'bg-primary-transparent text-primary border-e border-[#B9D7DF]'
              }`}
            >
              {label ?? 'Any'}
              <i
                className={`icon ${picking ? 'icon-chevron-up' : 'icon-chevron-down'} text-[13px]`}
              />
            </button>

            {code !== null ? (
              <>
                <span className="text-default font-mono-nums flex items-center ps-4 pe-0.5 text-[20px] font-semibold">
                  {byNumber ? 'RN' : code}-
                </span>
                <input
                  ref={box}
                  inputMode={byNumber ? 'text' : 'numeric'}
                  autoComplete="off"
                  aria-label={
                    byNumber ? 'The shop’s number on the tag' : `Digits on the ${code} tag`
                  }
                  placeholder={byNumber ? 'number on the tag' : '3601 for 36-01'}
                  value={digits}
                  disabled={disabled}
                  onChange={(event) => {
                    // The shop's numbers may carry letters, "-" and "/"; a design's tag only digits.
                    setDigits(
                      byNumber
                        ? event.target.value.toUpperCase().replace(/[^A-Z0-9/-]/g, '')
                        : event.target.value.replace(/\D/g, ''),
                    );
                    setSaid(null);
                  }}
                  onKeyDown={(event) => {
                    // Backspace on an empty box goes back to Any, as on the phone.
                    if (event.key === 'Backspace' && digits === '') {
                      choose(null);
                    }
                  }}
                  className={`font-mono-nums min-w-0 flex-1 bg-transparent pe-2 text-[20px] font-semibold tracking-wider text-gray-900 placeholder:font-normal placeholder:tracking-normal placeholder:text-gray-400 ${plain}`}
                />
              </>
            ) : (
              <>
                <i className="icon icon-search text-default self-center ps-3" aria-hidden="true" />
                <input
                  ref={box}
                  autoComplete="off"
                  aria-label="A design, a garment code or a scan"
                  placeholder="A design, a garment code — or scan a tag"
                  value={query}
                  disabled={disabled}
                  onChange={(event) => {
                    onQuery(event.target.value);
                    setSaid(null);
                  }}
                  className={`min-w-0 flex-1 bg-transparent px-3 text-[15px] text-gray-900 ${plain}`}
                />
              </>
            )}

            {(digits !== '' || query !== '') && (
              <button
                type="button"
                aria-label="Clear"
                onClick={() => {
                  setDigits('');
                  onQuery('');
                  box.current?.focus();
                }}
                className="text-default flex w-11 cursor-pointer items-center justify-center"
              >
                <i className="icon icon-x" />
              </button>
            )}
          </div>

          {picking && (
            <div
              role="dialog"
              aria-label="Choose the design"
              className="absolute start-0 top-[52px] z-[9999] w-[540px] max-w-[calc(100vw-48px)] overflow-hidden rounded-[14px] border shadow-lg"
              style={{ background: 'var(--overlay-surface)', borderColor: 'var(--overlay-border)' }}
            >
              <div className="border-border-color border-b p-3">
                <input
                  autoFocus
                  value={filter}
                  onChange={(event) => setFilter(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                      event.stopPropagation();
                      setPicking(false);
                    } else if (event.key === 'Enter') {
                      event.preventDefault();
                      event.stopPropagation();
                      if (names[0]) {
                        choose(names[0].code);
                      }
                    }
                  }}
                  data-enter-key-owns
                  aria-label="Filter designs"
                  placeholder="Type a short name or a design — bj, sherwani"
                  className="border-primary h-10 w-full rounded-lg border bg-white px-3 text-[14px] text-gray-900 outline-none"
                />
              </div>
              <ul className="m-0 max-h-[400px] list-none overflow-y-auto p-1.5 ps-1.5">
                <li className="list-none">
                  <button
                    type="button"
                    onClick={() => choose(null)}
                    className="flex w-full cursor-pointer items-center gap-3 rounded-lg p-2.5 text-start hover:bg-light"
                  >
                    <span className="w-[52px] shrink-0 text-center font-bold text-gray-900">
                      Any
                    </span>
                    <span className="text-default text-[13px]">
                      A design’s name, a whole code, or a scan
                    </span>
                  </button>
                </li>
                <li className="list-none">
                  <button
                    type="button"
                    onClick={() => choose(byReference)}
                    className={`flex w-full cursor-pointer items-center gap-3 rounded-lg p-2.5 text-start hover:bg-light ${
                      byNumber ? 'bg-primary-transparent' : ''
                    }`}
                  >
                    <span className="text-primary w-[52px] shrink-0 text-center font-bold">RN</span>
                    <span className="text-default text-[13px]">
                      The shop’s own number on the tag — RN-7
                    </span>
                  </button>
                </li>
                {names.map((row, index) => (
                  <li key={row.code} className="list-none">
                    <button
                      type="button"
                      onClick={() => choose(row.code)}
                      className={`flex w-full cursor-pointer items-center gap-3 rounded-lg p-2 text-start hover:bg-light ${
                        row.code === code || (index === 0 && filter !== '')
                          ? 'bg-primary-transparent'
                          : ''
                      }`}
                    >
                      <span className="w-[52px] shrink-0 overflow-hidden rounded-lg">
                        <DesignPhoto url={row.imageUrl} alt="" categoryPath="" />
                      </span>
                      <span className="text-primary min-w-24 shrink-0 font-bold tracking-wide">
                        {row.code}
                      </span>
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate font-semibold text-gray-900">{row.name}</span>
                        <span className="text-default text-[12px]">
                          {row.free} free of {row.total}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
                {names.length === 0 && (
                  <li className="text-default list-none p-4 text-[13px]">
                    No short name or design has “{filter}”.
                  </li>
                )}
              </ul>
              <p className="border-border-color text-default m-0 border-t px-3.5 py-2 text-[11px]">
                Type to narrow · Enter picks the first · Esc closes
              </p>
            </div>
          )}
        </form>
      </div>

      <p className="text-default mt-2 mb-0 text-[12px]">
        {code === null
          ? 'Pick the design’s short name on the left, then type only the numbers on its tag — 3601 for 36-01. Or pick RN for the shop’s own number, or type a name or a whole code here; a scanner types into this box.'
          : byNumber
            ? digits === ''
              ? `Every garment with a shop’s number is listed (${matches.length}). Type the number to narrow it.`
              : matches.length === 0
                ? `No garment’s number starts with ${digits}.`
                : next && next.referenceNumber === normalizeReference(digits)
                  ? `Enter adds ${withReference(next.serialCode, next.referenceNumber)} for ${forWhom}.`
                  : `${matches.length} garment${matches.length === 1 ? '’s number starts' : 's’ numbers start'} with ${digits}.`
            : digits === ''
              ? `Every ${code} garment is listed. Type the numbers on the tag to narrow it.`
              : matches.length === 1 && next
                ? `One garment left — Enter adds ${next.serialCode} for ${forWhom}.`
                : `${matches.length} ${code} garment${matches.length === 1 ? ' starts' : 's start'} with ${digits}.`}
      </p>
      {said && (
        <p
          aria-live="polite"
          className={`mt-1 mb-0 text-[12.5px] ${said.tone === 'good' ? 'text-success' : 'text-danger'}`}
        >
          {said.message}
        </p>
      )}

      {code !== null && (chosen || byNumber) && (
        <>
          {chosen && (
            <div className="bg-light mt-4 flex items-center gap-3 rounded-xl p-3">
              <span className="w-14 shrink-0 overflow-hidden rounded-lg">
                <DesignPhoto url={chosen.imageUrl} alt="" categoryPath="" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="mb-0 font-semibold text-gray-900">{chosen.name}</p>
                <p className="text-default mb-0 text-[12px]">
                  {chosen.free} free of {chosen.total} for these days
                </p>
              </div>
            </div>
          )}

          {byNumber && matches.length > 0 && (
            <div className="mt-3 flex gap-1.5" role="group" aria-label="Which garments to list">
              {[
                { on: true, words: 'Free only' },
                { on: false, words: 'All' },
              ].map((chip) => (
                <button
                  key={chip.words}
                  type="button"
                  aria-pressed={freeOnly === chip.on}
                  onClick={() => setFreeOnly(chip.on)}
                  className={`h-8 cursor-pointer rounded-full border px-3 text-[13px] ${
                    freeOnly === chip.on
                      ? 'border-primary bg-primary-transparent text-primary font-semibold'
                      : 'border-border-color hover:bg-light bg-white text-gray-900'
                  }`}
                >
                  {chip.words}
                </button>
              ))}
            </div>
          )}

          {listed.length > 0 && (
            <div className="border-border-color mt-3 overflow-hidden rounded-2xl border bg-white">
              <div
                aria-hidden="true"
                className={`${rowGrid} text-default border-border-color bg-light hidden border-b px-4 py-2 text-[11px] font-semibold tracking-wider uppercase sm:grid`}
              >
                <span>Our code · shop’s no.</span>
                <span />
                <span>Design · size and colour</span>
                <span>These days</span>
                <span />
              </div>
              <ul className="m-0 list-none ps-0">
                {listed.slice(0, rowLimit).map((unit, index) => {
                  const state = stateOf(unit);
                  const highlighted = unit === next;
                  const cut = byNumber ? 0 : typedLength(unit.serialCode, code, digits);
                  const tail = unit.serialCode.slice(code.length + 1);

                  return (
                    <li
                      key={unit.unitId}
                      className={`${rowGrid} list-none items-center px-4 py-2.5 ${
                        index > 0 ? 'border-border-color border-t' : ''
                      } ${highlighted ? 'bg-primary-transparent shadow-[inset_3px_0_0_var(--color-primary)]' : ''}`}
                    >
                      {/*
                        Our code leads and the shop's number sits under it small (owner,
                        2026-10-28) — whichever was typed is the one marked in the accent.
                      */}
                      <span className="flex min-w-0 flex-col">
                        <span
                          className={`font-mono-nums truncate text-[16px] font-bold tracking-wide ${
                            highlighted ? 'text-primary' : 'text-gray-900'
                          }`}
                        >
                          {byNumber ? (
                            unit.serialCode
                          ) : (
                            <>
                              {code}-
                              <span className="bg-primary/15 text-primary rounded px-0.5">
                                {tail.slice(0, cut)}
                              </span>
                              {tail.slice(cut)}
                            </>
                          )}
                        </span>
                        {unit.referenceNumber && (
                          <span
                            className={`font-mono-nums text-[12px] ${
                              byNumber ? 'text-primary font-semibold' : 'text-default'
                            }`}
                          >
                            RN-{unit.referenceNumber}
                          </span>
                        )}
                      </span>
                      <span className="size-11 overflow-hidden rounded-lg">
                        <DesignPhoto
                          url={unit.imageUrl ?? null}
                          alt=""
                          categoryPath={unit.categoryName}
                        />
                      </span>
                      <span className="flex min-w-0 flex-col gap-1">
                        <span className="truncate text-[14px] font-semibold text-gray-900">
                          {unit.itemName}
                        </span>
                        <span className="flex flex-wrap gap-1.5">
                          {axisChips(axesOf.get(unit.unitId), unit.variantDisplayName).map(
                            (chip) => (
                              <span
                                key={chip.key}
                                className="border-border-color inline-flex h-6 items-center gap-1.5 rounded-md border bg-white px-2 text-[12px] text-gray-900"
                              >
                                {chip.colourHex && (
                                  <span
                                    aria-hidden="true"
                                    className="border-border-color size-2.5 rounded-full border"
                                    style={{ backgroundColor: chip.colourHex }}
                                  />
                                )}
                                {chip.label}
                              </span>
                            ),
                          )}
                        </span>
                      </span>
                      <span
                        className={`col-span-2 text-[13px] font-medium sm:col-span-1 ${
                          state.free
                            ? 'text-[color:var(--color-success)]'
                            : 'text-[color:var(--color-warning)]'
                        }`}
                      >
                        {state.free ? 'Free' : state.words}
                      </span>
                      <span className="flex justify-end">
                        {state.free ? (
                          <button
                            type="button"
                            disabled={disabled}
                            onClick={() => add(unit)}
                            aria-label={`Add ${withReference(unit.serialCode, unit.referenceNumber)} for ${forWhom}`}
                            className={`h-10 w-full cursor-pointer rounded-[10px] px-3.5 text-[13px] font-semibold sm:w-auto sm:min-w-32 ${
                              highlighted
                                ? 'bg-primary hover:bg-primary-hover text-white'
                                : 'border-primary/40 text-primary hover:bg-primary-transparent border bg-white'
                            }`}
                          >
                            {highlighted ? 'Add · Enter' : 'Add'}
                          </button>
                        ) : unit.heldBy?.holder && onChaseHold ? (
                          <button
                            type="button"
                            onClick={() => onChaseHold(unit)}
                            className="border-border-color hover:bg-light h-10 w-full cursor-pointer rounded-[10px] border bg-white px-3.5 text-[13px] text-gray-900 sm:w-auto"
                          >
                            See who has it
                          </button>
                        ) : null}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {listed.length > rowLimit && (
                <p className="text-default border-border-color m-0 border-t px-4 py-2.5 text-[13px]">
                  {listed.length - rowLimit} more — type more of the{' '}
                  {byNumber ? 'number' : 'digits'}.
                </p>
              )}
            </div>
          )}
          {listed.length === 0 && (
            <p className="mt-3 mb-0 rounded-[10px] bg-[color:var(--color-warning)]/10 p-4 text-[13px] text-[color:var(--color-warning)]">
              {byNumber
                ? matches.length > 0
                  ? 'None of these is free for these days. Choose All to see who has them.'
                  : digits === ''
                    ? 'No garment on the rail has a shop’s number yet. Give one on the garment’s page.'
                    : `No garment’s number starts with ${digits}. Check the tag, or clear and type again.`
                : `No ${code} garment starts with ${digits}. Check the tag, or clear and type again.`}
            </p>
          )}
        </>
      )}
    </div>
  );
}

/** The inputs inside the box draw no ring or border of their own; the box shows focus. */
const plain =
  'border-0 py-0 shadow-none outline-none focus:border-0 focus:shadow-none focus:ring-0 focus:outline-none';

/** Option A's columns: what was typed against, photo, design and code, these days, the button. */
const rowGrid =
  'grid grid-cols-[auto_44px_minmax(0,1fr)] gap-x-4 gap-y-2 sm:grid-cols-[170px_44px_minmax(0,1fr)_minmax(0,200px)_auto]';

/** Rows shown before asking for a narrower search. */
const rowLimit = 30;
